import { Text, View, TouchableOpacity } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useEffect, useState } from 'react';
import AuthPage, { AuthHeader, AuthInput as Input, AuthButton as SolidButton } from '@/components/General/AuthPage';
import logo from "@/assets/logo/Verdigiris.png"
import { useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message'
import { Keyboard } from 'react-native';
import { supabase } from '@/data/client';
import { Image } from 'expo-image';
import * as Device from 'expo-device';
import { registerDevice } from '@/utils/deviceRegistration';
import * as Sentry from '@sentry/react-native';
import * as LocalAuthentication from 'expo-local-authentication';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import usePlatform from '@/utils/hooks/usePlatform';
import { showAlert } from '@/utils/showAlert';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import useAuthStore from '@/data/store/useAuthStore';
import { acceptInvite } from '@/data/registrations';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { openOrgSignup } from '@/utils/orgSignup';
import { restoreSessionWithRetry } from '@/utils/authRefresh';

// Tab-root routes aren't meaningful return targets after login — force the dashboard
// instead of dropping users back onto e.g. the Settings tab. Deep content links
// (/people/123, /events/..., etc.) are still honored.
const isTabRootRoute = (path) =>
    !path ||
    path === '/' ||
    path === '/menu' ||
    path === '/search' ||
    path === '/notifications' ||
    path === '/settings' ||
    path.startsWith('/settings/');

const LoginScreen = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [signingIn, setSigningIn] = useState(false);

    const queryClient = useQueryClient()

    const {theme: {colors, scheme}} = useUnistyles();
    const { isWeb, isAndroid } = usePlatform();
    const router = useRouter();
    const {
        showMessage, redirectTo, inviteData: encodedInvite, action, '#': hash,
        access_token: accessTokenParam, refresh_token: refreshTokenParam,
        token_hash: tokenHashParam, error: errorParam, error_description: errorDescriptionParam,
    } = useLocalSearchParams();
    const authStore = useAuthStore();

    const checkAuth = async () => {
        // Bounded resilient restore — a transient network failure makes
        // getSession() resolve { session: null, error } without destroying the
        // persisted session. Retry a few times so a valid session on a flaky
        // network still auto-redirects, then fall through to the form.
        const session = await restoreSessionWithRetry({ maxAttempts: 3 });
        setIsAuthenticated(!!session?.user?.id);
        setIsCheckingAuth(false);
    }

    useEffect(() => {
        const first = (v) => Array.isArray(v) ? v[0] : v;

        const handleAuth = async () => {
            // Universal links can deliver the auth payload either as top-level
            // query params (more reliable — some OSes strip URL fragments) or
            // in the '#' fragment (reachchms:// and Supabase's default style).
            const params = new URLSearchParams(first(hash) || '')

            const authError = first(errorParam) || params.get('error')
            if (authError) {
                const description = first(errorDescriptionParam) || params.get('error_description')
                Toast.show({ type: 'error', text2: description || 'Your verification link is no longer valid. Please sign in or request a new one.' });
                setIsCheckingAuth(false);
                return;
            }

            const accessToken = first(accessTokenParam) || params.get('access_token')
            const refreshToken = first(refreshTokenParam) || params.get('refresh_token')
            const tokenHash = first(tokenHashParam) || params.get('token_hash')

            if (accessToken && refreshToken) {
                handleTokenSignIn(accessToken, refreshToken);
            } else if (tokenHash) {
                handleTokenHashSignIn(tokenHash);
            } else {
                checkAuth();
            }
        }

        handleAuth()
        getSavedUsername();
    }, [])

    useEffect(() => {
        authStore.setTheme(scheme);
    }, [scheme])

    // Shared post-session path for every link-driven sign-in (token pair,
    // token_hash OTP): Sentry identity, saved username, device registration,
    // cache invalidation, then into org selection.
    const completeSessionSignIn = async (session) => {
        Sentry.setUser({
            id: session.user.id,
            username: session.user.user_metadata?.firstName + ' ' + session.user.user_metadata?.lastName,
            email: session.user.email,
        });

        await AsyncStorage.setItem('CURRENT_USERNAME', session.user.email);

        await registerDevice(isWeb, isAndroid, session.user.id);

        queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });

        Toast.show({ type: 'success', text2: 'Your email has been verified! Welcome to Reach.' });
        router.replace('/auth/select-organization');
    }

    const handleTokenSignIn = async (access_token, refresh_token) => {
        try {
            const { data, error } = await supabase.auth.setSession({
                access_token,
                refresh_token,
            });

            if (error || !data?.session?.user?.id) {
                Toast.show({ type: 'error', text2: 'There was a problem verifying your account. Please sign in manually.' });
                setIsCheckingAuth(false);
                return;
            }

            await completeSessionSignIn(data.session);
        } catch {
            Toast.show({ type: 'error', text2: 'Something went wrong. Please sign in manually.' });
            setIsCheckingAuth(false);
        }
    }

    const handleTokenHashSignIn = async (token_hash) => {
        try {
            // Signup confirmation links carry type 'signup'; 'email' covers
            // generic email-verification links using the same token_hash form.
            let { data, error } = await supabase.auth.verifyOtp({ token_hash, type: 'signup' });
            if (error) {
                ({ data, error } = await supabase.auth.verifyOtp({ token_hash, type: 'email' }));
            }

            if (error || !data?.session?.user?.id) {
                Toast.show({ type: 'error', text2: 'There was a problem verifying your account. Please sign in manually.' });
                setIsCheckingAuth(false);
                return;
            }

            await completeSessionSignIn(data.session);
        } catch {
            Toast.show({ type: 'error', text2: 'Something went wrong. Please sign in manually.' });
            setIsCheckingAuth(false);
        }
    }

    const parseInviteData = (encoded) => {
        if (!encoded) return null;
        try {
            const parsed = JSON.parse(atob(encoded));
            if (new Date(parsed.expiresAt) > new Date()) return parsed;
        } catch {
            //nothing
        }
        return null;
    }

    useEffect(() => {
        if(showMessage == "confirmed"){
            Toast.show({
                text2: "Your email has been confirmed! You can now log in.",
                type: "success",
            })

            // If there's invite data, process it after confirmation
            if (encodedInvite) {
                processInviteAfterConfirmation();
            }
        }
    }, [showMessage, encodedInvite])

    const processInviteAfterConfirmation = async () => {
        try {
            const invite = parseInviteData(encodedInvite);
            const { data: session } = await supabase.auth.getSession();
            if (session?.session?.user?.id && invite) {
                await acceptInvite({ inviteID: invite.id, organizationID: invite.organizationID, personID: invite.personID, userID: session.session.user.id });
                Toast.show({ type: 'success', text2: 'You have been added to the organization!' });
            }
        } catch {
            // Error already handled in acceptInvite
        }
    }

    // Handle existing user clicking invite link (action=join)
    useEffect(() => {
        if (action === 'join' && encodedInvite && isAuthenticated) {
            handleExistingUserInvite();
        }
    }, [action, encodedInvite, isAuthenticated])

    const handleExistingUserInvite = async () => {
        try {
            const invite = parseInviteData(encodedInvite);
            if (!invite) {
                Toast.show({ type: 'error', text2: 'This invitation is invalid or has expired.' });
                return;
            }

            showAlert({
                title: `Join ${invite.organizationName}?`,
                subtitle: `You've been invited to join ${invite.organizationName}. Would you like to accept?`,
                confirmText: 'Join',
                onConfirm: async () => {
                    const { data: session } = await supabase.auth.getSession();
                    if (session?.session?.user?.id) {
                        await acceptInvite({ inviteID: invite.id, organizationID: invite.organizationID, personID: invite.personID, userID: session.session.user.id });
                        Toast.show({ type: 'success', text2: `Welcome to ${invite.organizationName}!` });
                        router.replace('/auth/select-organization');
                    }
                }
            });
        } catch {
            // Error already handled
        }
    }

    const getSavedUsername = async () => {
        const savedUsername = await AsyncStorage.getItem("CURRENT_USERNAME");
        setUsername(savedUsername)
    }

    const signIn = async (username, password) => {
        const {data, error} = await supabase.auth.signInWithPassword({
            email: username,
            password: password,
        })

        if(error){
            if(error.message == "Invalid login credentials"){
                return {
                    success: 0,
                    message: "Incorrect username or password."
                }
            }
            else if(error.message == "Network error"){
                return {
                    success: 0,
                    message: "Network Error! Check your network connection and try again."
                }
            }
            else if(error.message == "Email not confirmed"){
                return {
                    success: 0,
                    message: "So it looks like you haven't confirmed your email address. Check your inbox and click on the link to confirm your email."
                }
            }
            else{
                return {
                    success: 0,
                    message: "Uh-oh! We've run into some trouble signing you in... Try again in a bit."
                }
            }
        }

        const compatible = await LocalAuthentication.hasHardwareAsync();
        const biometricEnabled = await AsyncStorage.getItem("@enableBiometrics");

        Sentry.setUser({ 
            id: data?.user?.id,
            username: data?.user?.user_metadata.firstName + " " + data?.user?.user_metadata.lastName,
            email: data?.user?.email,
            organization_id: data?.user?.app_metadata?.organizationID ?? null
        });
        
        if(compatible && !biometricEnabled && biometricEnabled !== false && Device.isDevice){
            showAlert({
                title: "Enable Biometrics",
                subtitle: "Would you like to enable Face ID to login on this device?",
                cancelText: "No thanks",
                confirmText: "Yes, please",
                onCancel: async () => {
                    await AsyncStorage.setItem('@enableBiometrics', 'false');
                },
                onConfirm: () => {
                    LocalAuthentication.authenticateAsync().then(async auth => {
                        if(auth.success){
                            await AsyncStorage.setItem('@enableBiometrics', 'true');
                        }
                    });  
                },
            })
        }
        else if(!compatible || !Device.isDevice){
            await AsyncStorage.setItem('@enableBiometrics', 'false');
        }

        await registerDevice(isWeb, isAndroid, data?.user?.id);

        Sentry.logger.info("Successful Login");

        return {
            success: 1,
            message: "Success!"
        }
    
    }


    const submitHandler = async () => {
        Keyboard.dismiss();

        if(!username || !password){
            Toast.show({type: 'error', text2: "Woah! You need both a Username and a Password to sign in!"})
            return false
        }

        setSigningIn(true);
        const signInStatus = await signIn(username, password);

        if(signInStatus.success === 0){
            Toast.show({type: 'error', text2: signInStatus.message})
            setSigningIn(false);
            return false
        }
        else{
            await AsyncStorage.setItem("CURRENT_USERNAME", username);
            queryClient.invalidateQueries({queryKey: QueryKeys.people.loggedInUser()})
            setSigningIn(false);
            if(redirectTo && !isTabRootRoute(redirectTo)){
                router.replace(redirectTo)
            }
            else{
                router.replace("/auth/select-organization")
            }
        }
    }
    
    return isCheckingAuth
        ? <View style={styles.checkingAuth}><ActivityIndicator size="large" /></View> 
        : isAuthenticated
            ? <Redirect href={"/auth/select-organization"} /> 
            : (
            <AuthPage hasHeader={false} footer={
                <TouchableOpacity testID="signup-organization-link" onPress={() => openOrgSignup(isWeb)}>
                    <Text style={[styles.linkText, {color:colors.onSurfaceVariant, textAlign:"center", lineHeight:21}]}>Signing up your church? <Text style={styles.linkText}>Start on our website</Text></Text>
                </TouchableOpacity>
            }>
                        <View style={{justifyContent:"center", alignItems:"center", flexDirection:"row", marginBottom:20,}}>
                            <View style={{borderRadius:8, overflow:"hidden"}}>
                                <Image testID='logo' source={logo} style={{height:50, width:200}} contentFit='contain'/>
                            </View>
                        </View>

                        <View>
                            <AuthHeader title="Welcome back" description="Sign in to your Reach account." />
                        </View>
                        <View style={styles.inputContainer}>
                            <Input
                                title="Email"
                                autoCapitalize='none'
                                textContentType="username"
                                autoCorrect={false}
                                type="email"
                                autoComplete="email"
                                value={username}
                                onUpdate={(new_value) => {
                                    setUsername(new_value);
                                }}
                            />
                        </View>

                        <View style={styles.inputContainer}>
                            <Input
                                title="Password"
                                textContentType='password'
                                autoComplete='current-password'
                                secureTextEntry={true}
                                value={password}
                                onEnter={submitHandler}
                                onUpdate={(new_value) => {
                                    setPassword(new_value);
                                }}
                            />
                        </View>

                        <TouchableOpacity testID="forgot-password-link" style={{zIndex:2, flexDirection:"row", alignItems:"center", justifyContent:"flex-end", marginTop:10}} onPress={() => {
                            router.push("/auth/account/recover")
                        }}>
                            <View><Text style={styles.linkText}>Forgot Password?</Text></View>
                        </TouchableOpacity>

                        <View style={{width:"100%", marginTop:20}}>
                            <SolidButton testID="sign-in" type="primary" iconName="log-in" processing={signingIn} onPress={submitHandler} text="Sign In" size="md"/>
                        </View>

                        <TouchableOpacity testID="sign-up-link" style={{zIndex:2, flexDirection:"row", alignItems:"center", justifyContent:"center", marginTop:20}} onPress={() => {
                            router.push("/auth/account/create")
                        }}>
                            <View><Text style={[styles.linkText, {color:colors.text}]}>Don&apos;t have an account? <Text style={styles.linkText}>Sign Up</Text></Text></View>
                        </TouchableOpacity>
            </AuthPage>
    )
}

const styles = StyleSheet.create(({colors, fonts}) => ({
    checkingAuth: {
        flex:1, 
        justifyContent:"center", 
        alignItems:"center"
    },
    inputContainer: {
        width:"100%"
    },
    linkText: {
        color:colors.primary,
        fontFamily:fonts.body,
        fontSize:14,
        _web: {
            cursor:"pointer",
            _hover: {
                opacity:0.8
            }
        }
    }
}))

export default LoginScreen;